/**
 * A user's group membership, editable where it is read — on the `/users` list.
 *
 * ⚠️ **This is a MODAL-free editor for a field that has no detail page.** There
 * is no `/users/:id` route and no `getUser` in `api.ts`, so a user's membership
 * has, until now, been reachable only by opening the Edit modal. Membership is
 * the one thing on this page an administrator changes repeatedly — as people
 * join and leave the account-lead and CSM groups — and it now gates real 422s
 * on five endpoints, so the round trip through a form that also edits email,
 * role and password was the wrong shape for it.
 *
 * ## The `[]`-versus-absent rule is why this is not a generic inline control
 *
 * `groupIds` is a **whole-set REPLACE**: `[1, 3]` replaces, `[]` CLEARS every
 * membership, and an ABSENT key leaves it alone. One falsy value carrying two
 * opposite instructions, which is the shape this app has now met five times
 * (Xero's `ContactPersons`, `cityIds`, `inclusions`, `regionIds`, and this).
 *
 * So the payload is built by {@link changedGroupIds} and by nothing else. It
 * returns `null` for "unchanged" — a value a payload cannot express — so a
 * `groupIds: []` can be reached only by somebody removing the last chip from a
 * set that had one, which is the single case where clearing is what they asked
 * for. **Never assign to `groupIds` directly here.**
 *
 * That also settles what an unchanged toggle does: nothing is sent, so
 * re-selecting what was already there cannot fire a write.
 *
 * ## Why it saves on every toggle rather than on close
 *
 * Each write is a COMPLETE replace, so every intermediate state is a correct,
 * self-consistent membership rather than a partial one — there is no half-saved
 * state to protect against, which is what a "save on close" affordance normally
 * buys. **That is the whole reason saving on every toggle is safe.**
 * `MultiCombobox` also exposes no close event, so a debounce would have to
 * invent an unsaved state and then explain it. ⚠️ *SUPERSEDED, and it was never
 * the argument it looked like: "Three groups exist, so the realistic ceiling is
 * three requests against a 120/60s bucket."* The limit is **12,000/60s**
 * (corrected 2026-09-17), so three writes justify nothing either way — do not
 * reinstate a request count as the reason.
 *
 * ## Gating
 *
 * Mounted UNCONDITIONALLY and self-gating, like the three inline field
 * components on the revenue documents. `{canUpdate && <InlineUserGroups/>}` at
 * the call site would hide the VALUE from everyone who cannot write it — and
 * every authenticated role, `viewer` included, may read the staff list. A
 * reader without the permission sees the chips and the reason, never an absence.
 */
import { useCallback, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  changedGroupIds,
  groupMembershipOptions,
  groupsOf,
  refusedGroupIds,
  type UserGroupsState,
} from '@/lib/user-groups';
import type { User } from '@/lib/types';
import { MultiCombobox, ValuePills } from '@/components/ui';

/**
 * Every group as a wrapping pill, or a dash. The read-only half of the editor.
 * `ValuePills` rather than `Badge`, whose `whitespace-nowrap` would let one long
 * group name overflow the cell instead of wrapping (owner, 2026-09-25).
 */
export function GroupBadges({ row }: { row: User }) {
  const groups = groupsOf(row);
  return (
    <ValuePills
      items={groups.map((group) => ({ key: group.id, label: group.name }))}
      empty={<span className="text-fg-subtle">&mdash;</span>}
    />
  );
}

export interface InlineUserGroupsProps {
  row: User;
  /** The `/user-groups` master, read once by the page and shared by every row. */
  groups: UserGroupsState;
  /** `ability.canUpdate('users')` — writes to `/users` are admin-only. */
  canUpdate: boolean;
  /** `denialReason(...)`, rendered as text when `canUpdate` is false. */
  denial: string | null;
  /** The updated record the server returned, so the row re-renders from the wire. */
  onSaved: (user: User) => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string; refused: string[] };

export function InlineUserGroups({
  row,
  groups,
  canUpdate,
  denial,
  onSaved,
}: InlineUserGroupsProps) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  /*
   * The draft exists ONLY while a write is in flight or has failed. On success
   * it is dropped and the control reads the record again, so the row is always
   * showing what the server last confirmed rather than what was typed at it.
   * Keeping it after a failure is deliberate: the selection that was refused is
   * the one the person needs to correct.
   */
  const [draft, setDraft] = useState<string[] | null>(null);

  const stored = groupsOf(row).map((group) => String(group.id));
  const values = draft ?? stored;

  const save = useCallback(
    async (next: string[]) => {
      /*
       * ⚠️ The ONLY place a `groupIds` value is produced. `null` means the set
       * is unchanged, so nothing is sent — an empty PATCH is a 422, and more to
       * the point a write nobody asked for.
       */
      const ids = changedGroupIds(next, stored);
      if (ids === null) {
        setDraft(null);
        setStatus({ kind: 'idle' });
        return;
      }

      setDraft(next);
      setStatus({ kind: 'saving' });
      try {
        const updated = await api.updateUser(row.id, { groupIds: ids });
        setDraft(null);
        setStatus({ kind: 'saved' });
        onSaved(updated.data);
      } catch (caught) {
        /*
         * The 422 names EVERY unknown or inactive id in one message, so each
         * offending chip is marked rather than the control as a whole — the
         * person fixes all of them in one pass instead of resubmitting to
         * discover the next.
         *
         * `formMessage` before `message`: on a 422 the latter is only
         * "Validation failed".
         */
        const refused =
          caught instanceof ApiError ? refusedGroupIds(caught, next.map(String)) : [];
        const message =
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : 'The membership could not be saved.';
        setStatus({ kind: 'error', message, refused });
      }
    },
    [onSaved, row.id, stored],
  );

  if (!canUpdate) {
    /*
     * Read-only means plain chips, not a disabled control: a disabled picker
     * reads as something you have not earned the right to use, where text reads
     * as a fact. The reason rides on `title` rather than taking a line in every
     * row of the table.
     */
    return (
      <div {...(denial === null ? {} : { title: denial })}>
        <GroupBadges row={row} />
      </div>
    );
  }

  /*
   * A stored group the master no longer offers is injected back by
   * `groupMembershipOptions`, labelled, rather than dropped — a picker that
   * renders blank over a real value gets it silently cleared by the next save.
   */
  const options = groupMembershipOptions(groups.records, groupsOf(row));

  /*
   * Disabled only when there is genuinely nothing to work with: the master
   * failed or is not deployed AND this account holds nothing to remove. With
   * memberships in hand the control still has work to do — taking one away does
   * not need the master.
   */
  const unusable =
    (groups.failed || groups.unavailable) && groups.records.length === 0 && options.length === 0;

  return (
    <div className="grid gap-1">
      <MultiCombobox
        values={values}
        onChange={save}
        options={options}
        loading={groups.loading}
        disabled={unusable || status.kind === 'saving'}
        placeholder="No groups"
        searchPlaceholder="Filter groups"
        /*
         * Every group as a chip, wrapping — owner's decision 2026-09-25: a list
         * cell shows every value, no "N groups" count and no "+N more". ⚠️
         * *SUPERSEDED: "Count-only, as in the line grids".* The row grows
         * instead. `summaryNoun` stays: it still names the trigger for a screen
         * reader ("2 groups").
         */
        maxVisibleChips={Number.POSITIVE_INFINITY}
        summaryNoun={{ singular: 'group', plural: 'groups' }}
        invalid={status.kind === 'error'}
        {...(status.kind === 'error' ? { invalidValues: status.refused } : {})}
        {...(unusable
          ? { footnote: 'The user-groups list could not be read, so membership cannot be changed.' }
          : {})}
        /*
         * ⚠️ The footnote above SUPPRESSES this row, and here that is the
         * interlock doing something it was not designed for and getting it
         * right anyway: it exists to stop a "select all" over a TRUNCATED list,
         * and the only footnote this mount passes reports a list that could not
         * be READ at all. Selecting every option of a list we do not have is
         * the worse version of the same lie, so the suppression stands.
         */
        selectAll
        aria-label={`User groups for ${row.name}`}
      />
      {status.kind === 'saving' && <span className="text-xs text-fg-muted">Saving…</span>}
      {status.kind === 'saved' && <span className="text-xs text-fg-muted">Saved</span>}
      {status.kind === 'error' && (
        <span role="alert" className="text-xs break-words text-danger">
          {status.message}
        </span>
      )}
    </div>
  );
}
